import React, { useEffect, useState } from 'react';

import { Button } from '@/design-system/button';
import { FormModal } from '@/design-system/form-modal';
import { DispatchDetailItem } from '@/services/api/dispatches';

/** Terminal item dispositions — item-level defer no longer exists. */
type TerminalItemDisposition = 'CANCEL' | 'LOST';

interface CancelItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The item being disposed. Null = modal closed. */
  item: DispatchDetailItem | null;
  /** Apply the chosen disposition. CANCEL/LOST are terminal (items endpoint). */
  onSubmit: (
    item: DispatchDetailItem,
    disposition: TerminalItemDisposition,
    reason: string
  ) => Promise<void>;
}

/**
 * Dispose of a single item on a PREVIEW dispatch — cancel/lost, both terminal.
 * Item-scoped mirror of the stop Remove modal. (Defer is stop-scoped only, so
 * there's no defer option here.)
 */
const CancelItemModal: React.FC<CancelItemModalProps> = ({
  isOpen,
  onClose,
  item,
  onSubmit,
}) => {
  const [choice, setChoice] = useState<TerminalItemDisposition>('CANCEL');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setChoice('CANCEL');
      setReason('');
    }
  }, [isOpen]);

  const CHOICES: Array<{
    key: TerminalItemDisposition;
    label: string;
    hint: string;
  }> = [
    {
      key: 'CANCEL',
      label: 'Batalkan item (Cancel)',
      hint: 'Item dibatalkan (status Cancelled) dan keluar dari dispatch. Terminal — tidak bisa dikembalikan.',
    },
    {
      key: 'LOST',
      label: 'Tandai item hilang (Lost)',
      hint: 'Item ditandai hilang (status Lost) dan keluar dari dispatch. Pakai untuk barang yang tidak akan pernah datang.',
    },
  ];

  const handleSubmit = async () => {
    if (!item) return;
    setSubmitting(true);
    try {
      await onSubmit(item, choice, reason.trim());
    } finally {
      setSubmitting(false);
    }
  };

  const actionLabel = choice === 'CANCEL' ? 'Batalkan item' : 'Tandai hilang';

  return (
    <FormModal
      open={isOpen}
      onClose={onClose}
      icon={<i className="ri-alert-line" />}
      iconTone="danger"
      title="Remove item"
      subtitle={item ? item.itemName : undefined}
      size="md"
      closeOnBackdropClick={!submitting}
      closeOnEscape={!submitting}
      footer={
        <div className="flex items-center justify-end gap-2 w-full">
          <Button tone="neutral" style="stroke" onClick={onClose} disabled={submitting}>
            Batal
          </Button>
          <Button
            tone="destructive"
            onClick={handleSubmit}
            disabled={submitting || !item}
          >
            {submitting ? 'Memproses…' : actionLabel}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {item && (
          <div className="rounded-lg border border-stroke-soft-200 bg-bg-weak-50 px-3 py-2.5">
            <p className="font-mono text-[12px] font-semibold text-text-strong-950 tabular-nums break-words">
              {item.waybill}
            </p>
            <p className="mt-0.5 text-[11px] text-text-sub-600">
              {item.itemName} · {item.weightKg.toFixed(2)} kg
            </p>
          </div>
        )}

        <div>
          <p className="mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
            Tindakan untuk item ini
          </p>
          <div className="rounded-lg border border-stroke-soft-200 divide-y divide-stroke-soft-200">
            {CHOICES.map((c) => {
              const checked = choice === c.key;
              return (
                <label
                  key={c.key}
                  className={`flex items-start gap-3 px-3 py-2.5 cursor-pointer transition-colors ${
                    checked ? 'bg-bg-weak-50 border-l-2 border-l-accent' : 'border-l-2 border-l-transparent hover:bg-bg-weak-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="item-disposition"
                    checked={checked}
                    onChange={() => setChoice(c.key)}
                    disabled={submitting}
                    className="mt-0.5 h-4 w-4 border-stroke-soft-200 text-accent focus:ring-accent"
                  />
                  <span className="min-w-0">
                    <span className="block text-[13px] font-semibold text-text-strong-950 tracking-tight">
                      {c.label}
                    </span>
                    <span className="block text-[12px] text-text-sub-600 leading-relaxed">
                      {c.hint}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        <div>
          <label
            htmlFor="item-disposition-reason"
            className="mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600"
          >
            Alasan (opsional)
          </label>
          <textarea
            id="item-disposition-reason"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Contoh: barang batal dikirim…"
            disabled={submitting}
            className="w-full rounded-lg border border-stroke-soft-200 bg-white px-3 py-2 text-sm text-text-strong-950 focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent disabled:bg-bg-soft-200 disabled:cursor-not-allowed disabled:text-text-sub-600"
          />
        </div>
      </div>
    </FormModal>
  );
};

export default CancelItemModal;
